import { fetchAuthSession } from 'aws-amplify/auth'
import type { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import Axios from 'axios'

import { env } from 'src/config/env'
import { logger } from 'src/lib/logger'
import { convertKeysToCamelCase } from 'src/shared/utils/case-conversion'

const API_BASE_URL = `${env.BACKEND_URL}`

const getCurrentAccessToken = async (): Promise<string | undefined> => {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.accessToken.toString()
    return token
  } catch (error) {
    logger.error('Error fetching access token:', error)
    return undefined
  }
}

interface ReqError extends Error {
  response?: {
    data: {
      message?: string
      detail?: string
    }
  }
}

class RouteErrorResponse extends Error {
  constructor(public status: number, public statusText: string, public data: string) {
    super(statusText)
  }
}

const handleReqError = (error: ReqError, defaultMessage: string): never => {
  if (Axios.isAxiosError(error) && error.response) {
    const errorMessage = error.response.data.message || error.response.data.detail || defaultMessage
    const status = error.response.status || 500
    throw new RouteErrorResponse(status, errorMessage, errorMessage)
  }
  logger.error('Unexpected error:', error)
  throw new RouteErrorResponse(500, 'Internal Server Error', 'An unexpected error occurred')
}

const axiosClient = Axios.create({
  baseURL: API_BASE_URL,
})

axiosClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await getCurrentAccessToken()
  config.headers.Authorization = `Bearer ${token}`
  config.headers['Content-Type'] = 'application/json'

  return config
})

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    response.data = convertKeysToCamelCase(response.data)
    return response
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  },
)

export const getRequest = async <T>(url: string, params?: object): Promise<T | undefined> => {
  try {
    const response = await axiosClient.get<T>(url, { params })
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while fetching data from ${url}`)
    return undefined
  }
}

export const postRequest = async <T>(url: string, data?: object): Promise<T | undefined> => {
  try {
    const response = await axiosClient.post<T>(url, data)
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while posting data to ${url}`)
    return undefined
  }
}

export const putRequest = async <T>(url: string, data?: object): Promise<T | undefined> => {
  try {
    const response = await axiosClient.put<T>(url, data)
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while updating data at ${url}`)
    return undefined
  }
}

export const deleteRequest = async <T>(url: string): Promise<T | undefined> => {
  try {
    const response = await axiosClient.delete<T>(url)
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while deleting data at ${url}`)
    return undefined
  }
}
